/**
 * TableLayoutEditor — JEDEN edytor układu kolumn tabeli osadzonej w formatce rekordu,
 * używany na płótnie kreatora formatek przez każdy typ tabeli (tabele dodatkowe, pozycje
 * dokumentu, rejestr VAT, widoki SQL, podlisty…). Wzorowany na starym „ColumnChooser"
 * z WF-owego web.
 *
 * Pokazuje kartę: tytuł po lewej, przycisk „Kolumny" po prawej, pod spodem pasek nagłówków
 * (tylko kolumny WIDOCZNE) z jednym statycznym wierszem przykładowym. Kontrakt `settings`
 * (`tableColumns` = układ bazowy, kolejność tablicy = kolejność kolumn; `hiddenColumnIds` =
 * dodatkowa maska / nakładka tenanta) i czyste operacje na układzie żyją w `@saas/shared`
 * (`embedded-table-columns`) — ten komponent tylko je woła. KAŻDA akcja użytkownika liczy
 * nowy układ operacją ze `@saas/shared` i wysyła JEDNĄ łatkę `onChange(buildTableColumnsPatch(…))`;
 * komponent niczego sam nie zapisuje. Układ do łatki liczony jest z NAJNOWSZYCH propsów
 * (`useLatestRef`), także w handlerach gestów na `document`.
 *
 * Tryby (`mode`):
 *  - `full` (układ własny) — przeciąganie nagłówka (myszą i klawiaturą) zmienia kolejność,
 *    uchwyt na prawej krawędzi zmienia szerokość (myszą albo strzałkami lewo/prawo, krok 10 px),
 *    strzałka w nagłówku otwiera menu kolumny (podpis, wyrównanie, miejsca po przecinku,
 *    ukrycie), panel „Kolumny" ma przeciąganie i „Przywróć domyślne". W obu trybach nie da się
 *    ukryć OSTATNIEJ widocznej kolumny (`lastVisibleEmbeddedTableColumnId` ze `@saas/shared`):
 *    tabela bez kolumn nie pokazałaby wierszy, więc nie dałoby się ich zobaczyć ani usunąć.
 *  - `hideOnly` (układ systemowy — tenant może tylko ukrywać) — bez przeciągania, zmiany
 *    szerokości i menu nagłówka; panel „Kolumny" ma same checkboxy. Płótno kreatora jest wtedy
 *    nieklikalne (`pointer-events: none` na przodku), więc przycisk i panel same przywracają
 *    klikalność.
 *
 * Bez `onChange` komponent jest podglądem tylko do odczytu: tytuł, nagłówki i wiersz przykładowy,
 * żadnych kontrolek. Korzeń przyjmuje `className`/`style` — kreator klonuje go, by nałożyć style
 * elementu. Klik i wciśnięcie myszy w kontrolkach nie bąbelkują do kreatora (nie zaznaczają całego
 * elementu i nie zaczynają jego przeciągania).
 *
 * Opcjonalnie panel „Kolumny" ma w stopce przycisk „dodaj kolumnę" (`addColumn`; panel pokazuje go
 * tylko w trybie `full`) —
 * konsument sam otwiera własne okno dodawania (np. pole dodatkowe), a kolumna nowego pola pojawia
 * się potem przez zmianę `descriptors` (jak każda kolumna spoza zapisanego układu: znaczek „nowa").
 * Deskryptor może nieść `editorNote` (np. „tylko w edycji") — pokazywany znaczkiem w panelu
 * i podpowiedzią na nagłówku.
 *
 * Popovery (panel „Kolumny" i menu kolumn) mają JEDEN stan otwarcia w tym komponencie: otwarcie
 * jednego zamyka pozostałe, a początek zmiany szerokości zamyka wszystkie (zatrzymane `pointerdown`
 * nie dochodzi do Radix, więc „klik poza" sam by tego nie zrobił).
 *
 * Przeciąganie nagłówków ma WŁASNY `DndContext` (zagnieżdżony w kontekście kreatora, jak
 * `FormatkaReorderList`). Zmiana szerokości startuje od ZAPISANEJ szerokości kolumny (a dla kolumny
 * o szerokości auto — od zmierzonej), trzyma szerokość w lokalnym stanie na czas gestu i zapisuje
 * tylko przeciąganą kolumnę, raz, po puszczeniu przycisku. Wskaźnik jest przechwytywany przez
 * uchwyt, a po gestie, który zmienił szerokość, połykamy jeden następny klik — inaczej klik po
 * puszczeniu przycisku zaznaczałby element w kreatorze. Gdy choć jedna kolumna ma jawną
 * szerokość, tabela przechodzi na układ stały: kolumny z jawną szerokością mają `<col>` w pikselach,
 * kolumny auto — `<col>` bez szerokości, a `minWidth` tabeli (`fixedTableMinWidth`) sprawia, że
 * w węższym kontenerze tabela rośnie i kontener się przewija (kolumny auto nie zapadają się do 0 px).
 */

import {
  type CSSProperties,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  buildTableColumnsPatch,
  effectiveColumnAlign,
  EMBEDDED_TABLE_COLUMN_WIDTH_MAX,
  EMBEDDED_TABLE_COLUMN_WIDTH_MIN,
  type EmbeddedTableColumnDescriptor,
  type EmbeddedTableColumnPatch,
  type EmbeddedTableColumnsWriteMode,
  type EmbeddedTableLegacyReader,
  lastVisibleEmbeddedTableColumnId,
  moveEmbeddedTableColumn,
  patchEmbeddedTableColumn,
  type ResolvedEmbeddedTableColumn,
  resolveEmbeddedTableColumns,
  setEmbeddedTableColumnVisible,
} from '@saas/shared';

import {
  columnAlignClass,
  fixedTableMinWidth,
  formatDecimalCell,
  TableColgroup,
} from '@/lib/embedded-table/cells';
import { isDragInProgressInScopes } from '@/lib/overlay-stack';
import { cn } from '@/lib/utils';

import { restrictToHorizontalAxis } from './axis-modifiers';
import { ColumnsPanel } from './ColumnsPanel';
import { GripHandle } from './GripHandle';
import { HeaderMenu } from './HeaderMenu';
import { stopBubble, suppressNextClick, useLatestRef } from './interaction-helpers';
import { useDndAccessibility } from './use-dnd-accessibility';

export interface TableLayoutEditorProps {
  /** Kolumny dostępne w tabeli (dostarcza konsument: definicja tabeli, pozycji dokumentu…). */
  descriptors: readonly EmbeddedTableColumnDescriptor[];
  /** `element.settings` umieszczenia tabeli na formatce. */
  settings: Record<string, unknown> | undefined;
  /** Brak = tylko podgląd (np. podgląd tylko do odczytu w kreatorze). */
  onChange?: (patch: Record<string, unknown>) => void;
  /** `full` — układ własny (cały `tableColumns`); `hideOnly` — układ systemowy (tylko maska). */
  mode: EmbeddedTableColumnsWriteMode;
  title?: ReactNode;
  /** Czytnik starszego formatu układu (gdy brak `tableColumns`). */
  legacy?: EmbeddedTableLegacyReader;
  /** Klucze starszego formatu, które zapis `full` i „Przywróć domyślne" czyszczą. */
  legacyKeys?: readonly string[];
  className?: string;
  style?: CSSProperties;
  /** Prefiks `data-testid` (domyślnie `table-layout-editor`). */
  testIdPrefix?: string;
  /**
   * Przycisk „dodaj kolumnę" w stopce panelu „Kolumny" (np. „Dodaj pole dodatkowe"). Pokazywany
   * tylko w trybie `full` z `onChange`; klik zamyka panel i woła `onClick` — samo okno dodawania
   * montuje wołający (np. w `children`), bo panel (popover) zamyka się razem z kliknięciem.
   */
  addColumn?: { label: string; onClick: () => void };
  /**
   * Dodatkowa zawartość korzenia edytora, bez wpływu na układ karty — miejsce na okna wołane
   * z `addColumn` (renderują się w portalu, ale zdarzenia React bąbelkują przez ich rodzica).
   * Edytor owija ją w niewidoczny element (`display: contents`), który zatrzymuje kliknięcie
   * i wciśnięcie wskaźnika — okno nie zaznacza i nie zaczyna przeciągania całego elementu
   * kreatora, więc wołający nie musi tego robić sam. Wciśnięcie myszy i klawisze celowo dochodzą
   * do `document` (zamknięcia list „klik poza" w oknach).
   */
  children?: ReactNode;
}

const HORIZONTAL_MODIFIERS = [restrictToHorizontalAxis];

/** Krok zmiany szerokości strzałkami lewo/prawo na uchwycie (px). */
const RESIZE_KEY_STEP = 10;

/** Klucze `settings` układu własnego — „Przywróć domyślne" je czyści (obok kluczy legacy). */
const LAYOUT_KEYS = ['tableColumns', 'hiddenColumnIds'] as const;

/** Wartości wiersza przykładowego (stałe — podgląd ma być deterministyczny). */
const SAMPLE_DECIMAL = 1234.5;
const SAMPLE_INTEGER = 3;
const SAMPLE_DATE = new Date(2026, 0, 15);

const TH_CLASS =
  'relative border-b border-border-secondary bg-bg-secondary px-2 py-1.5 text-xs font-medium text-text-secondary';

/** Który popover jest otwarty: panel „Kolumny" albo menu jednej kolumny (naraz najwyżej jeden). */
type OpenMenu = { kind: 'columns' } | { kind: 'column'; id: string };

function isSameMenu(a: OpenMenu, b: OpenMenu): boolean {
  if (a.kind === 'columns') return b.kind === 'columns';
  return b.kind === 'column' && b.id === a.id;
}

function clampWidth(width: number): number {
  return Math.min(
    EMBEDDED_TABLE_COLUMN_WIDTH_MAX,
    Math.max(EMBEDDED_TABLE_COLUMN_WIDTH_MIN, Math.round(width)),
  );
}

/** Czy w `settings` jest cokolwiek, co „Przywróć domyślne" miałoby wyczyścić. */
function hasStoredLayout(
  settings: Record<string, unknown> | undefined,
  legacyKeys: readonly string[] | undefined,
): boolean {
  if (settings === undefined) return false;
  return [...LAYOUT_KEYS, ...(legacyKeys ?? [])].some((key) => settings[key] !== undefined);
}

/** Wartość przykładowa komórki wg rodzaju kolumny (dziesiętne respektują `decimals`). */
function sampleValue(col: ResolvedEmbeddedTableColumn, textSample: string): string {
  // Format `pl-PL` odwzorowuje formatowanie na stronie rekordu (tam też zaszyty na stałe).
  switch (col.descriptor.kind) {
    case 'decimal': {
      const formatted = formatDecimalCell(SAMPLE_DECIMAL, col.decimals);
      return formatted ?? SAMPLE_DECIMAL.toLocaleString('pl-PL');
    }
    case 'integer':
      return String(SAMPLE_INTEGER);
    case 'date':
      return SAMPLE_DATE.toLocaleDateString('pl-PL');
    case 'boolean':
      return '✓';
    case 'text':
      return textSample;
    default:
      return '—';
  }
}

/** Sterowanie nagłówkiem dostępne tylko w trybie `full` z `onChange`. */
interface HeaderControls {
  /** Id kolumny, której menu jest otwarte (`null` = żadne). */
  openColumnId: string | null;
  onMenuOpenChange: (colId: string, open: boolean) => void;
  onResizeStart: (colId: string, event: ReactPointerEvent<HTMLElement>) => void;
  onResizeKeyDown: (colId: string, event: ReactKeyboardEvent<HTMLElement>) => void;
  onResizeFocus: (colId: string, event: ReactFocusEvent<HTMLElement>) => void;
  onResizeBlur: () => void;
  /** Bieżąca szerokość dla `aria-valuenow` uchwytu (zapisana/na żywo, a dla auto — zmierzona przy fokusie). */
  resizeValueNow: (col: ResolvedEmbeddedTableColumn) => number | undefined;
  onPatch: (colId: string, patch: EmbeddedTableColumnPatch) => void;
  onHide: (colId: string, pending: EmbeddedTableColumnPatch) => void;
  onHideFocus: () => void;
  /** Id jedynej widocznej kolumny — jej menu nie oferuje „Ukryj kolumnę" (`null` = brak takiej). */
  lastVisibleId: string | null;
}

interface HeaderCellProps {
  col: ResolvedEmbeddedTableColumn;
  testIdPrefix: string;
  /** `null` = nagłówek bez kontrolek (tryb `hideOnly` albo sam podgląd). */
  controls: HeaderControls | null;
}

/** Zawartość nagłówka: [uchwyt] podpis [strzałka menu] + uchwyt zmiany szerokości. */
function HeaderCellContent({
  col,
  testIdPrefix,
  controls,
  grip,
}: HeaderCellProps & { grip: ReactNode }) {
  const { t } = useTranslation();
  const name = col.label ?? col.descriptor.label;
  const alignClass = columnAlignClass(effectiveColumnAlign(col));
  const headerId = `${testIdPrefix}-header-${col.id}`;
  // Pusta adnotacja = brak adnotacji (nie pusty `title` ani pusty tekst dla czytnika ekranu).
  const editorNote = col.descriptor.editorNote || undefined;

  return (
    <>
      <div className="flex min-w-0 items-center gap-1">
        {grip}
        <span className={cn('min-w-0 flex-1 truncate', alignClass)} title={editorNote}>
          {name}
          {/* `title` czytnik ekranu zwykle pomija — adnotację dajemy też jako tekst tylko dla niego. */}
          {editorNote ? <span className="sr-only"> ({editorNote})</span> : null}
        </span>
        {controls && (
          <HeaderMenu
            col={col}
            open={controls.openColumnId === col.id}
            onOpenChange={(open) => controls.onMenuOpenChange(col.id, open)}
            onPatch={(patch) => controls.onPatch(col.id, patch)}
            onHide={(pending) => controls.onHide(col.id, pending)}
            onHideFocus={controls.onHideFocus}
            canHide={controls.lastVisibleId !== col.id}
            testId={`${headerId}-menu`}
          />
        )}
      </div>
      {controls && (
        <span
          role="separator"
          aria-orientation="vertical"
          aria-label={t('tenant.embeddedTable.resizeColumnFor', { name })}
          aria-valuenow={controls.resizeValueNow(col)}
          aria-valuemin={EMBEDDED_TABLE_COLUMN_WIDTH_MIN}
          aria-valuemax={EMBEDDED_TABLE_COLUMN_WIDTH_MAX}
          tabIndex={0}
          onPointerDown={(event) => controls.onResizeStart(col.id, event)}
          onKeyDown={(event) => controls.onResizeKeyDown(col.id, event)}
          onFocus={(event) => controls.onResizeFocus(col.id, event)}
          onBlur={controls.onResizeBlur}
          onMouseDown={(event) => event.preventDefault()}
          onClick={stopBubble}
          className="absolute right-0 top-0 h-full w-2 cursor-col-resize touch-none select-none hover:bg-accent-500/40 focus-visible:bg-accent-500/60 focus-visible:outline-none"
          data-testid={`${headerId}-resize`}
        />
      )}
    </>
  );
}

/** Nagłówek bez przeciągania (tryb `hideOnly` / podgląd). */
function StaticHeaderCell(props: HeaderCellProps) {
  return (
    <th
      scope="col"
      className={TH_CLASS}
      data-testid={`${props.testIdPrefix}-header-${props.col.id}`}
    >
      <HeaderCellContent {...props} grip={null} />
    </th>
  );
}

/** Nagłówek przeciągalny (tryb `full`): uchwyt w komórce, sama komórka zachowuje rolę nagłówka. */
function SortableHeaderCell(props: HeaderCellProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.col.id,
  });
  const name = props.col.label ?? props.col.descriptor.label;

  return (
    <th
      ref={setNodeRef}
      scope="col"
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(TH_CLASS, isDragging && 'z-10 bg-bg-elevated opacity-80 shadow')}
      data-testid={`${props.testIdPrefix}-header-${props.col.id}`}
    >
      <HeaderCellContent
        {...props}
        grip={
          <span className="inline-flex shrink-0" onClick={stopBubble} onPointerDown={stopBubble}>
            <GripHandle
              ariaLabel={t('tenant.embeddedTable.dragColumn', { name })}
              attributes={attributes}
              listeners={listeners}
              data-testid={`${props.testIdPrefix}-header-${props.col.id}-drag`}
            />
          </span>
        }
      />
    </th>
  );
}

export function TableLayoutEditor({
  descriptors,
  settings,
  onChange,
  mode,
  title,
  legacy,
  legacyKeys,
  className,
  style,
  testIdPrefix = 'table-layout-editor',
  addColumn,
  children,
}: TableLayoutEditorProps) {
  const { t } = useTranslation();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  // Szerokość kolumny na czas gestu zmiany szerokości (zapis dopiero po puszczeniu przycisku).
  const [liveWidth, setLiveWidth] = useState<{ id: string; width: number } | null>(null);
  // Szerokość zmierzona przy fokusie uchwytu kolumny o szerokości auto (dla `aria-valuenow`).
  const [measuredWidth, setMeasuredWidth] = useState<{ id: string; width: number } | null>(null);
  const [openMenu, setOpenMenu] = useState<OpenMenu | null>(null);
  const resizeCleanupRef = useRef<(() => void) | null>(null);
  const columnsTriggerRef = useRef<HTMLButtonElement>(null);
  // Korzeń edytora i treść panelu „Kolumny" (w portalu) — jedyne miejsca, w których uchwyt
  // przeciągania „należy do tego edytora" (przeciąganie gdzie indziej na stronie nic nie blokuje).
  const rootRef = useRef<HTMLDivElement>(null);
  const columnsPanelContentRef = useRef<HTMLDivElement>(null);

  const resolved = useMemo(
    () => resolveEmbeddedTableColumns(descriptors, settings, legacy ? { legacy } : undefined),
    [descriptors, settings, legacy],
  );
  // Handlery gestów na `document` i efekty czytają najnowszy układ, nie ten z chwili startu gestu.
  const latest = useLatestRef({ resolved, onChange, mode, legacyKeys });

  // Odpięcie nasłuchu gestu zmiany szerokości, gdyby edytor zniknął w jego trakcie.
  useEffect(() => () => resizeCleanupRef.current?.(), []);

  const editable = onChange !== undefined;
  // Pełne sterowanie nagłówkiem — tylko układ własny i tylko z możliwością zapisu.
  const fullControls = editable && mode === 'full';

  /** Liczy nowy układ z NAJNOWSZYCH propsów i wysyła jedną łatkę `settings`. */
  const emitWith = (
    update: (
      cols: readonly ResolvedEmbeddedTableColumn[],
      currentMode: EmbeddedTableColumnsWriteMode,
    ) => readonly ResolvedEmbeddedTableColumn[],
  ): void => {
    const current = latest.current;
    current.onChange?.(
      buildTableColumnsPatch(update(current.resolved, current.mode), current.mode, {
        legacyKeys: current.legacyKeys,
      }),
    );
  };

  /** Czy trwa przeciąganie dnd-kit z uchwytem w TYM edytorze (nagłówek) albo jego panelu „Kolumny". */
  const isEditorDragInProgress = (): boolean => {
    const scopes: HTMLElement[] = [];
    if (rootRef.current) scopes.push(rootRef.current);
    if (columnsPanelContentRef.current) scopes.push(columnsPanelContentRef.current);
    return isDragInProgressInScopes(scopes);
  };

  /**
   * Otwarcie zamyka pozostałe popovery; zamknięcie nie rusza menu, które już zdążyło się otworzyć.
   * W trakcie przeciągania (klawiaturą w panelu „Kolumny") stan się nie zmienia: odmontowanie
   * panelu zostawiłoby czujnik dnd-kit aktywny na `document` — najpierw trzeba dokończyć lub
   * anulować przeciąganie.
   */
  const setMenuOpen = (menu: OpenMenu, open: boolean): void => {
    if (isEditorDragInProgress()) return;
    setOpenMenu((current) => {
      if (open) return menu;
      return current !== null && isSameMenu(current, menu) ? null : current;
    });
  };

  const handleToggleVisible = (id: string, visible: boolean): void => {
    emitWith((cols, currentMode) => setEmbeddedTableColumnVisible(cols, id, visible, currentMode));
  };

  const handleMove = (activeId: string, overId: string): void => {
    emitWith((cols) => moveEmbeddedTableColumn(cols, activeId, overId));
  };

  const handleReset = (): void => {
    const current = latest.current;
    // Klucze układu własnego i starszych formatów znikają ze `settings` po scaleniu łatki.
    const patch: Record<string, unknown> = {};
    for (const key of [...LAYOUT_KEYS, ...(current.legacyKeys ?? [])]) patch[key] = undefined;
    current.onChange?.(patch);
  };

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    handleMove(String(active.id), String(over.id));
  };

  const handleResizeStart = (colId: string, event: ReactPointerEvent<HTMLElement>): void => {
    // Tylko główny przycisk (lewy / dotyk / rysik): prawy i środkowy nie zaczynają gestu.
    if (event.button !== 0) return;
    // Gest zmiany szerokości nie może ruszyć przeciągania nagłówka ani zaznaczać tekstu.
    event.stopPropagation();
    event.preventDefault();
    const col = latest.current.resolved.find((candidate) => candidate.id === colId);
    if (!col) return;

    // Zatrzymane `pointerdown` nie dochodzi do Radix — otwarte popovery zamykamy sami, ale nie
    // w trakcie przeciągania (zamknięty panel zostawiłby czujnik dnd-kit aktywny).
    if (!isEditorDragInProgress()) setOpenMenu(null);

    resizeCleanupRef.current?.();
    const separator = event.currentTarget;
    // Start od ZAPISANEJ szerokości; zmierzona z nagłówka tylko dla kolumny o szerokości auto.
    const measured = Math.round(separator.closest('th')?.getBoundingClientRect().width ?? 0);
    const startWidth = clampWidth(col.width ?? measured);
    const startX = event.clientX;
    let currentWidth = startWidth;

    // Uchwyt przechwytuje wskaźnik: ruch i puszczenie trafiają do niego także poza jego obrysem,
    // więc końcowy klik nie ląduje na losowym elemencie pod kursorem. Przeglądarka odrzuca
    // przechwycenie (`DOMException`, np. `NotFoundError`), gdy wskaźnik nie jest już aktywny albo
    // zdarzenie jest syntetyczne — gest działa wtedy dalej przez nasłuch na `document`
    // (po zmianie szerokości połyka klik `suppressNextClick`), więc błąd DOM jest tu świadomie
    // pomijany; każdy inny błąd propaguje.
    try {
      separator.setPointerCapture(event.pointerId);
    } catch (error) {
      if (!(error instanceof DOMException)) throw error;
    }

    const handleMoveGesture = (moveEvent: PointerEvent): void => {
      currentWidth = clampWidth(startWidth + moveEvent.clientX - startX);
      setLiveWidth({ id: colId, width: currentWidth });
    };
    const cleanup = (): void => {
      document.removeEventListener('pointermove', handleMoveGesture);
      document.removeEventListener('pointerup', handleUp);
      document.removeEventListener('pointercancel', handleCancel);
      separator.removeEventListener('lostpointercapture', handleCancel);
      resizeCleanupRef.current = null;
    };
    const handleUp = (): void => {
      cleanup();
      setLiveWidth(null);
      // Zapis raz, na koniec gestu — i tylko gdy szerokość faktycznie się zmieniła.
      if (currentWidth === startWidth) return;
      suppressNextClick();
      // Zapisujemy szerokość tylko przeciąganej kolumny (pozostałe zostają auto) — jak WF; decyzja
      // z 2026-10-21 (docs/OPEN_QUESTIONS.md, „Tabele osadzone w formularzach").
      emitWith((cols) => patchEmbeddedTableColumn(cols, colId, { width: currentWidth }));
    };
    const handleCancel = (): void => {
      cleanup();
      setLiveWidth(null);
    };

    document.addEventListener('pointermove', handleMoveGesture);
    document.addEventListener('pointerup', handleUp);
    document.addEventListener('pointercancel', handleCancel);
    separator.addEventListener('lostpointercapture', handleCancel);
    resizeCleanupRef.current = cleanup;
  };

  /** Strzałki lewo/prawo na uchwycie: krok 10 px, jeden zapis na krok. */
  const handleResizeKeyDown = (colId: string, event: ReactKeyboardEvent<HTMLElement>): void => {
    const delta =
      event.key === 'ArrowRight'
        ? RESIZE_KEY_STEP
        : event.key === 'ArrowLeft'
          ? -RESIZE_KEY_STEP
          : 0;
    if (delta === 0) return;
    event.preventDefault();
    event.stopPropagation();
    const col = latest.current.resolved.find((candidate) => candidate.id === colId);
    if (!col) return;
    const base =
      col.width ??
      Math.round(event.currentTarget.closest('th')?.getBoundingClientRect().width ?? 0);
    const next = clampWidth(base + delta);
    if (next === col.width) return;
    // Jak po geście myszą (`handleUp`): zapis tylko tej jednej kolumny.
    emitWith((cols) => patchEmbeddedTableColumn(cols, colId, { width: next }));
  };

  const handleResizeFocus = (colId: string, event: ReactFocusEvent<HTMLElement>): void => {
    const measured = Math.round(
      event.currentTarget.closest('th')?.getBoundingClientRect().width ?? 0,
    );
    setMeasuredWidth(measured > 0 ? { id: colId, width: measured } : null);
  };

  const controls: HeaderControls | null = fullControls
    ? {
        openColumnId: openMenu?.kind === 'column' ? openMenu.id : null,
        onMenuOpenChange: (colId, open) => setMenuOpen({ kind: 'column', id: colId }, open),
        onResizeStart: handleResizeStart,
        onResizeKeyDown: handleResizeKeyDown,
        onResizeFocus: handleResizeFocus,
        onResizeBlur: () => setMeasuredWidth(null),
        resizeValueNow: (col) =>
          col.width ?? (measuredWidth?.id === col.id ? measuredWidth.width : undefined),
        onPatch: (colId, patch) => emitWith((cols) => patchEmbeddedTableColumn(cols, colId, patch)),
        // Szkice pól menu i ukrycie wychodzą JEDNĄ łatką, liczoną z bieżącego układu.
        onHide: (colId, pending) =>
          emitWith((cols, currentMode) =>
            setEmbeddedTableColumnVisible(
              Object.keys(pending).length > 0
                ? patchEmbeddedTableColumn(cols, colId, pending)
                : cols,
              colId,
              false,
              currentMode,
            ),
          ),
        onHideFocus: () => columnsTriggerRef.current?.focus(),
        lastVisibleId: lastVisibleEmbeddedTableColumnId(resolved),
      }
    : null;

  const displayColumns = resolved
    .filter((col) => col.visible)
    .map((col) => (liveWidth?.id === col.id ? { ...col, width: liveWidth.width } : col));
  const textSample = t('tenant.embeddedTable.sampleText');
  const dndAccessibility = useDndAccessibility(
    displayColumns.map((col) => ({ id: col.id, name: col.label ?? col.descriptor.label })),
  );

  // Menu kolumny, której nagłówka już nie ma (ukryta, zmiana trybu), nie może zostać „otwarte"
  // w stanie — po ponownym pokazaniu kolumny wyskoczyłoby samo.
  const openColumnGone =
    openMenu?.kind === 'column' &&
    !(fullControls && displayColumns.some((col) => col.id === openMenu.id));
  useEffect(() => {
    if (openColumnGone) setOpenMenu(null);
  }, [openColumnGone]);

  // Zmierzona szerokość kolumny, której już nie ma wśród widocznych (ukryta, zmiana układu),
  // nie może przeżyć: po ponownym pokazaniu kolumny wróciłaby jako nieaktualna `aria-valuenow`.
  const measuredGone =
    measuredWidth !== null && !displayColumns.some((col) => col.id === measuredWidth.id);
  useEffect(() => {
    if (measuredGone) setMeasuredWidth(null);
  }, [measuredGone]);

  // Układ stały (`table-layout: fixed`) tylko przy jawnej szerokości choć jednej kolumny; minimalna
  // szerokość sprawia, że gdy kontener jest węższy, tabela rośnie i kontener się przewija —
  // kolumny auto (`<col>` bez szerokości) nie zapadają się do 0 px.
  const minWidth = fixedTableMinWidth(displayColumns);

  const table = (
    <table
      className="w-full border-collapse text-sm"
      style={minWidth === undefined ? undefined : { tableLayout: 'fixed', minWidth }}
    >
      <TableColgroup columns={displayColumns} />
      <thead>
        <tr>
          {displayColumns.map((col) =>
            fullControls ? (
              <SortableHeaderCell
                key={col.id}
                col={col}
                testIdPrefix={testIdPrefix}
                controls={controls}
              />
            ) : (
              <StaticHeaderCell
                key={col.id}
                col={col}
                testIdPrefix={testIdPrefix}
                controls={controls}
              />
            ),
          )}
        </tr>
      </thead>
      <tbody>
        <tr>
          {displayColumns.map((col) => (
            <td
              key={col.id}
              className={cn(
                'truncate px-2 py-1.5 text-text-muted',
                columnAlignClass(effectiveColumnAlign(col)),
              )}
              data-testid={`${testIdPrefix}-sample-${col.id}`}
            >
              {sampleValue(col, textSample)}
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );

  return (
    <div
      ref={rootRef}
      className={cn('rounded-lg border border-border-secondary bg-bg-primary p-3', className)}
      style={style}
      data-testid={testIdPrefix}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1 truncate text-sm font-medium text-text-primary">{title}</div>
        {editable && (
          <ColumnsPanel
            resolved={resolved}
            mode={mode}
            open={openMenu?.kind === 'columns'}
            onOpenChange={(open) => setMenuOpen({ kind: 'columns' }, open)}
            onToggleVisible={handleToggleVisible}
            onMove={handleMove}
            onReset={handleReset}
            canReset={hasStoredLayout(settings, legacyKeys)}
            triggerRef={columnsTriggerRef}
            panelRef={columnsPanelContentRef}
            testIdPrefix={testIdPrefix}
            addColumn={addColumn}
          />
        )}
      </div>

      <div className="min-w-0 overflow-x-auto" data-testid={`${testIdPrefix}-strip`}>
        {displayColumns.length === 0 ? (
          <p className="px-2 py-3 text-sm text-text-muted">
            {descriptors.length === 0
              ? t('tenant.embeddedTable.noColumns')
              : t('tenant.embeddedTable.allHidden')}
          </p>
        ) : fullControls ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={HORIZONTAL_MODIFIERS}
            accessibility={dndAccessibility}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={displayColumns.map((col) => col.id)}
              strategy={horizontalListSortingStrategy}
            >
              {table}
            </SortableContext>
          </DndContext>
        ) : (
          table
        )}
      </div>
      {children ? (
        // Zatrzymujemy WYŁĄCZNIE `click` i `pointerdown`: zaznaczenie bloku w kreatorze idzie przez
        // `onClick` (`LayoutElementBlock`), a przeciąganie startuje z własnego uchwytu. `mousedown`
        // i `keydown` MUSZĄ dojść do `document` — na nich wiszą zamknięcia list „klik poza”
        // (`SearchableSelect`, `MultiSelect`) w oknach przenoszonych portalem do tego poddrzewa.
        <div className="contents" onClick={stopBubble} onPointerDown={stopBubble}>
          {children}
        </div>
      ) : null}
    </div>
  );
}
